/**
 * The app run id (APP-MONITORING M0.3).
 *
 * WHAT IT IS. One UUIDv7 minted when a client process starts, sent on every
 * request that client makes, and carried by every crash event, usage event and
 * problem report it produces. It is the thread that ties "this launch of the
 * app" together: given a report reference, an operator can pull the crash that
 * preceded it and the requests around it because all three name the same run.
 *
 * WHAT IT IS NOT — and this is the part worth guarding. It is NOT a device id,
 * NOT a user id, and NOT a session id. It is minted fresh on every process
 * start and **never persisted**: nothing writes it to storage, so it cannot be
 * correlated across launches, and on the patient client it cannot outlive the
 * tab. That is deliberate. A stable per-device identifier for an unauthenticated
 * patient surface would be a tracking identifier attached to people who never
 * agreed to be tracked — the ADR-0009 posture — and once persisted it would be
 * one migration away from becoming a join key onto clinical rows. Ephemeral by
 * construction is cheaper to keep true than ephemeral by policy.
 *
 * It is therefore safe to log and safe to put in telemetry: it identifies a
 * RUN, not a person. The scrubber's allow-lists (M0.4) name it for that reason.
 */

import { uuidv7 } from './ids';

/** The header both clients send it on. */
export const APP_RUN_ID_HEADER = 'x-app-run-id';

/**
 * Minted once, at module load — i.e. at process start, which is what the
 * requirement means by "per run". Module scope rather than a lazy getter so
 * there is no window in which two callers could mint different ids, and no
 * setter that a later caller could use to rotate it mid-run (a rotating run id
 * would silently break every correlation it exists to provide).
 */
const APP_RUN_ID: string = uuidv7();

export function appRunId(): string {
  return APP_RUN_ID;
}
